// Client-side validation for a hand-typed YYYY-MM-DD range. Shared by the
// partner dashboard's global range picker, the campaign drilldown's date
// controls and the staff screens' RangeSelect, so all refuse the same inputs.
//
// The staff screens' range helpers live here too: a range is either a
// preset id ('today', '7d', ...) or a custom `{ from, to }` of New York
// days.

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const MIN_DATE = '2000-01-01'

// A range has to round-trip through Date before it can reach a request:
// anything malformed stops here as an on-screen error and no call goes out.
// Some endpoints (apply_date_filter) swallow an unparseable date and answer
// with the entire history; /partner/dashboard 422s instead. Either way the
// partner is better served by a message next to the input.
export function isValidIsoDate(s) {
  if (!ISO_DATE_RE.test(s)) return false
  const d = new Date(`${s}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
}

// `max` (YYYY-MM-DD, optional) is the last day allowed - nyToday() on the
// staff screens, whose backend refuses a day after today.
export function rangeProblem(from, to, { max } = {}) {
  if (!isValidIsoDate(from)) return "Start date isn't a real date."
  if (!isValidIsoDate(to)) return "End date isn't a real date."
  if (from < MIN_DATE || to < MIN_DATE) return 'Dates before 2000 are not supported.'
  // Safe as a string compare: both sides are known-valid YYYY-MM-DD.
  if (from > to) return 'Start date must be on or before the end date.'
  if (max && to > max) return "End date can't be in the future."
  return null
}

const NY_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
})

// Today in New York as YYYY-MM-DD - the business's day, whatever the
// browser's zone. From parts rather than format(): en-CA's order is not
// something to depend on.
export function nyToday(now = new Date()) {
  const part = Object.fromEntries(NY_DAY.formatToParts(now).map((p) => [p.type, p.value]))
  return `${part.year}-${part.month}-${part.day}`
}

// ── staff screens: a range is a preset id or { from, to } ───────────────

export const isCustomRange = (range) => typeof range === 'object' && range !== null

// What the backend reads: `range`, plus the dates for a custom one.
export const rangeParams = (range) => (
  isCustomRange(range) ? { range: 'custom', date_from: range.from, date_to: range.to } : { range }
)

// The query-string keys: ?from=&to= for a custom range, ?range= for a
// preset other than the screen's default, nothing for the default.
export const rangeSearch = (range, defaultRange) => {
  if (isCustomRange(range)) return { from: range.from, to: range.to }
  return range !== defaultRange ? { range } : {}
}

// A valid ?from=&to= wins over ?range=. Anything malformed, backwards or in
// the future falls back to the preset, then the default - never a request
// the backend would refuse.
export function parseUrlRange(searchParams, presets, defaultRange, today = nyToday()) {
  const from = searchParams.get('from')
  const to = searchParams.get('to')
  if (from && to && !rangeProblem(from, to, { max: today })) return { from, to }
  const range = searchParams.get('range')
  return presets.some((p) => p.id === range) ? range : defaultRange
}
